import { LoadingButton } from "@mui/lab";
import { Card, CardContent, CardHeader, Grid, Tab, Box, Typography, Chip, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Button, List, ListItem, ListItemText, ListItemIcon } from "@mui/material";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import { useForm } from "react-hook-form";
import { FormProvider, RHFFileField, RHFTextField } from "../../common/hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from 'yup';
import DownloadSampleFile from "../../components/DownloadSampleFile";
import UploadIcon from '@mui/icons-material/Upload';
import { useState, useEffect } from "react";
import useTabParam from '../../hooks/useTabParam';
import { apiConstants, get, post } from "../../common/constants";
import { useSnackbar } from "notistack";
import PageTitle from "../../components/PageTitle";

function PodsUploads() {
    const [isSubmittingBulk, setIsSubmittingBulk] = useState(false)
    const [isSubmittingBulkZip, setIsSubmittingBulkZip] = useState(false)
    const [isSubmittingAwb, setIsSubmittingAwb] = useState(false)
    const [activeTab, setActiveTab] = useTabParam("1")
    const [supportedFormats, setSupportedFormats] = useState([])
    const [maxFileSize, setMaxFileSize] = useState(null)
    const [loadingFormats, setLoadingFormats] = useState(true)
    const [uploadResults, setUploadResults] = useState(null)
    const [showResultsModal, setShowResultsModal] = useState(false)
    const sampleName = 'BULK_POD'
    const { enqueueSnackbar } = useSnackbar()

    // Bulk upload form
    const bulkMethods = useForm({
        resolver: yupResolver(Yup.object().shape({
            file: Yup.mixed().required('Please select a file')
        })),
        defaultValues: {
            file: ''
        }
    })
    const bulkMethodsZip = useForm({
        resolver: yupResolver(Yup.object().shape({
            file: Yup.mixed().required('Please select a file')
        })),
        defaultValues: {
            file: ''
        }
    })

    // AWB upload form with filename validation
    const awbMethods = useForm({
        resolver: yupResolver(Yup.object().shape({
            awb: Yup.string().required('AWB number is required'),
            file: Yup.mixed()
                .required('Please select a file')
                .test('filename-format', 'Filename must start with the AWB number', function (value) {
                    const awb = this.parent.awb;
                    if (!value || !value[0] || !awb) return true; // Let required validation handle empty cases

                    const filename = value[0].name;
                    return filename.toLowerCase().startsWith(awb.toLowerCase());
                })
        })),
        defaultValues: {
            awb: '',
            file: ''
        }
    })

    const { handleSubmit: handleBulkSubmit, reset: resetBulk } = bulkMethods
    const { handleSubmit: handleAwbSubmit, reset: resetAwb } = awbMethods
    const { handleSubmit: handleBulkSubmitZip, reset: resetBulkZip } = bulkMethodsZip

    // Fetch supported file formats on component mount
    useEffect(() => {
        const fetchSupportedFormats = async () => {
            try {
                const response = await get(apiConstants.FILE_CONVERSION_FORMATS)
                if (response.data && response.data.status === 'SUCCESS' && response.data.responseObj) {
                    setSupportedFormats(response.data.responseObj.supportedExtensions || [])
                    setMaxFileSize(response.data.responseObj.maxFileSizeMB || null)
                } else {
                    setSupportedFormats([])
                    setMaxFileSize(null)
                }
            } catch (error) {
                console.error('Error fetching supported formats:', error)
                enqueueSnackbar('Failed to load supported file formats', { variant: 'warning' })
                setSupportedFormats([])
                setMaxFileSize(null)
            } finally {
                setLoadingFormats(false)
            }
        }

        fetchSupportedFormats()
    }, [])

    const onBulkSubmit = async (data) => {
        setIsSubmittingBulk(true)
        let formData = new FormData()
        formData.append('file', data?.file[0])
        try {
            await post(apiConstants.UPLOADS_PODS, formData).then((res) => {
                if (res.data === '[Success]') {
                    enqueueSnackbar('Successfully uploaded!', { variant: 'success' })
                }
                else {
                    enqueueSnackbar(res.data, { variant: 'error' })
                }
            })
        }
        catch (err) {
            const message = err.response?.data?.message || err.message
            enqueueSnackbar(message, { variant: 'error' })
        }
        resetBulk()
        setIsSubmittingBulk(false)
    }
    const onBulkSubmitZip = async (data) => {
        setIsSubmittingBulkZip(true)
        let formData = new FormData()
        formData.append('file', data?.file[0])
        try {
            await post(apiConstants.UPLOAD_BULK_PODS_ZIP, formData).then((res) => {
                if (res.data === '[Success]') {
                    enqueueSnackbar('Successfully uploaded!', { variant: 'success' })
                }
                else if (res.data?.responseObj) {
                    setUploadResults(res.data.responseObj)
                    setShowResultsModal(true)
                    const { successfulUploads, failedUploads } = res.data.responseObj
                    if (successfulUploads > 0 && failedUploads === 0) {
                        enqueueSnackbar(`All ${successfulUploads} files uploaded successfully!`, { variant: 'success' })
                    } else if (successfulUploads > 0 && failedUploads > 0) {
                        enqueueSnackbar(`${successfulUploads} files uploaded, ${failedUploads} failed. Check details below.`, { variant: 'error' })
                    } else {
                        enqueueSnackbar(`Upload completed with ${failedUploads} errors. Check details below.`, { variant: 'error' })
                    }
                }
                else {
                    enqueueSnackbar(res.data, { variant: 'error' })
                }
            })
        }
        catch (err) {
            const message = err.response?.data?.message || err.message
            enqueueSnackbar(message, { variant: 'error' })
        }
        resetBulkZip()
        setIsSubmittingBulkZip(false)
    }

    const onAwbSubmit = async (data) => {
        setIsSubmittingAwb(true)
        let formData = new FormData()
        formData.append('file', data?.file[0])
        formData.append('awb', data?.awb)
        formData.append('uploadType', 'single') // Flag to indicate single AWB upload

        try {
            // First upload to file conversion service
            const conversionResponse = await post(apiConstants.FILE_CONVERSION_UPLOAD, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            })

            if (conversionResponse.status === 200 && conversionResponse.data) {
                if (conversionResponse.data.status === 'SUCCESS') {
                    enqueueSnackbar('POD uploaded successfully', { variant: 'success' })
                    //const { fileName, fileSize, mimeType, s3Url } = conversionResponse.data.responseObj;

                    // Then process with existing POD upload API
                    // const podFormData = new FormData()
                    // podFormData.append('file', data?.file[0])
                    // podFormData.append('awb', data?.awb)
                    // podFormData.append('uploadType', 'single')
                    // // Add conversion details for reference
                    // podFormData.append('convertedFileName', fileName)
                    // podFormData.append('s3Url', s3Url)

                    // await post(apiConstants.UPLOADS_PODS, podFormData).then((res) => {
                    //    if(res.data === '[Success]'){
                    //     enqueueSnackbar(`POD uploaded successfully for AWB: ${data.awb} (${fileName}, ${(fileSize/1024/1024).toFixed(2)}MB)`, { variant: 'success' })
                    //    }
                    //    else{
                    //     enqueueSnackbar(res.data, { variant: 'error' })
                    //    }
                    // })
                } else if (conversionResponse.data.status === 'ERROR') {
                    // Handle API error response
                    const errorMessage = conversionResponse.data.responseObj || 'POD Upload failed';
                    enqueueSnackbar(errorMessage, { variant: 'error' })
                } else {
                    enqueueSnackbar('Unexpected response from POD Upload service', { variant: 'error' })
                }
            } else {
                const errorMessage = conversionResponse.data?.message || 'POD Upload failed';
                enqueueSnackbar(`POD Upload failed for AWB: ${data.awb} - ${errorMessage}`, { variant: 'error' })
            }
        }
        catch (err) {
            const message = err.response?.data?.message || err.message
            enqueueSnackbar(message, { variant: 'error' })
        }
        resetAwb()
        setIsSubmittingAwb(false)
    }

    return (
        <>
            <PageTitle>POD Upload</PageTitle>
            <TabContext value={activeTab}>
                <Box sx={{ backgroundColor: '#fff', boxShadow: 1 }}>
                    <TabList onChange={(_e, newValue) => setActiveTab(newValue)}>
                        <Tab label="Bulk POD Upload" value="1" />
                        <Tab label="Single AWB POD Upload" value="2" />
                    </TabList>
                </Box>

                <TabPanel value="1" sx={{ px: 0 }}>
                    {activeTab === "1" && (
                        <>
                            <Card>
                                <CardHeader title='Upload Bulk PODs Links' subheader='Upload PODs for multiple AWBs via a file upload.' />
                                <CardContent>
                                    <FormProvider methods={bulkMethods} onSubmit={handleBulkSubmit(onBulkSubmit)}>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <RHFFileField name='file' />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <LoadingButton
                                                    loading={isSubmittingBulk}
                                                    variant="contained"
                                                    size="medium"
                                                    startIcon={<UploadIcon />}
                                                    type="submit"
                                                >
                                                    Upload Bulk PODs
                                                </LoadingButton>
                                                <DownloadSampleFile fileName={sampleName} />
                                            </Grid>
                                        </Grid>
                                    </FormProvider>
                                </CardContent>
                            </Card>
                            <Card sx={{ mt: 2 }}>
                                <CardHeader title='Upload Bulk PODs Zip File' subheader='Upload PODs for multiple AWBs via a ZIP file upload.' />
                                <CardContent>
                                    <Alert severity="info" sx={{ mb: 2 }}>
                                        <Typography variant="body2">
                                            <strong>How to create zip file:</strong>
                                            <br />
                                            • Create a folder and add all your POD files (images/documents)
                                            <br />
                                            • Right-click on the folder and select "Send to" → "Compressed (zipped) folder" (Windows) or "Compress" (Mac)
                                            <br />
                                            • Alternatively, use any zip software like WinRAR, 7-Zip, or online zip tools
                                            <br />
                                            • The user must upload a ZIP file containing the POD files, each named with the LR number and having a .jpg or .png extension (e.g., LRnumber.jpg or LRnumber.png).
                                        </Typography>
                                    </Alert>
                                    <FormProvider methods={bulkMethodsZip} onSubmit={handleBulkSubmitZip(onBulkSubmitZip)}>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <RHFFileField name='file' />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <LoadingButton
                                                    loading={isSubmittingBulkZip}
                                                    variant="contained"
                                                    size="medium"
                                                    startIcon={<UploadIcon />}
                                                    type="submit"
                                                >
                                                    Upload Bulk PODs Zip
                                                </LoadingButton>
                                                {/* <DownloadSampleFile fileName={sampleName} /> */}
                                            </Grid>
                                        </Grid>
                                    </FormProvider>
                                </CardContent>
                            </Card>
                        </>
                    )}
                </TabPanel>

                <TabPanel value="2" sx={{ px: 0 }}>
                    {activeTab === "2" && (
                        <Card>
                            <CardHeader
                                title='Upload Single AWB POD'
                                subheader='Upload POD for a specific AWB. The filename must start with the AWB number (e.g., AWB123.pdf). Files will be automatically converted to supported formats.'
                            />
                            <CardContent>
                                {!loadingFormats && supportedFormats.length > 0 && (
                                    <Grid container spacing={2} sx={{ mb: 2 }}>
                                        <Grid item xs={12}>
                                            <Alert severity="info" sx={{ mb: 2 }}>
                                                <Typography variant="body2" gutterBottom>
                                                    <strong>Max File Size: {maxFileSize}MB</strong>
                                                </Typography>
                                            </Alert>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography variant="body2" color="textSecondary" gutterBottom>
                                                Supported file extensions:
                                            </Typography>
                                            {supportedFormats.map((format, index) => (
                                                <Chip
                                                    key={index}
                                                    label={`.${format.toUpperCase()}`}
                                                    size="small"
                                                    variant="outlined"
                                                    color="primary"
                                                    sx={{ mr: 1, mb: 1 }}
                                                />
                                            ))}
                                        </Grid>
                                    </Grid>
                                )}
                                <FormProvider methods={awbMethods} onSubmit={handleAwbSubmit(onAwbSubmit)}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={6}>
                                            <RHFTextField
                                                name='awb'
                                                label='AWB Number'
                                                placeholder='Enter AWB number'
                                                fullWidth
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <RHFFileField
                                                name='file'
                                                helperText="Filename must start with the AWB number (e.g., AWB123.pdf, AWB123.jpg). File will be automatically converted if needed."
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <LoadingButton
                                                loading={isSubmittingAwb}
                                                variant="contained"
                                                size="medium"
                                                startIcon={<UploadIcon />}
                                                type="submit"
                                            >
                                                Upload AWB POD
                                            </LoadingButton>
                                        </Grid>
                                    </Grid>
                                </FormProvider>
                            </CardContent>
                        </Card>
                    )}
                </TabPanel>
            </TabContext>

            {/* Upload Results Modal */}
            <Dialog
                open={showResultsModal}
                onClose={() => setShowResultsModal(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" alignItems="center" gap={1}>
                        <InfoIcon color="primary" />
                        <Typography variant="h6">Upload Results</Typography>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {uploadResults && (
                        <Box>
                            {/* Summary Information */}
                            <Alert severity="info" sx={{ mb: 2 }}>
                                <Typography variant="body2">
                                    <strong>File:</strong> {uploadResults.zipFileName} ({Math.round(uploadResults.zipFileSize / 1024)} KB)
                                    <br />
                                    <strong>Total Files:</strong> {uploadResults.totalFilesExtracted}
                                    <br />
                                    <strong>Successful:</strong> {uploadResults.successfulUploads}
                                    <br />
                                    <strong>Failed:</strong> {uploadResults.failedUploads}
                                    <br />
                                    <strong>Processed At:</strong> {new Date(uploadResults.processedAt).toLocaleString()}
                                </Typography>
                            </Alert>

                            {/* Success Results */}
                            {uploadResults.results && uploadResults.results.length > 0 && (
                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="h6" color="success" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <CheckCircleIcon />
                                        Successful Uploads ({uploadResults.results.length})
                                    </Typography>
                                    <List dense>
                                        {uploadResults.results.map((result, index) => (
                                            <ListItem key={index}>
                                                <ListItemIcon>
                                                    <CheckCircleIcon color="success" />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={result.fileName || `File ${index + 1}`}
                                                    secondary={result.message || 'Uploaded successfully'}
                                                />
                                            </ListItem>
                                        ))}
                                    </List>
                                </Box>
                            )}

                            {/* Error Results */}
                            {uploadResults.errors && uploadResults.errors.length > 0 && (
                                <Box>
                                    <Typography variant="h6" color="error" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <ErrorIcon />
                                        Failed Uploads ({uploadResults.errors.length})
                                    </Typography>
                                    <List dense>
                                        {uploadResults.errors.map((error, index) => (
                                            <ListItem key={index}>
                                                <ListItemIcon>
                                                    <ErrorIcon color="error" />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={error.split(':')[0] || `Error ${index + 1}`}
                                                    secondary={error.split(':').slice(1).join(':').trim() || error}
                                                />
                                            </ListItem>
                                        ))}
                                    </List>
                                </Box>
                            )}

                            {/* No results case */}
                            {(!uploadResults.results || uploadResults.results.length === 0) &&
                                (!uploadResults.errors || uploadResults.errors.length === 0) && (
                                    <Alert severity="warning">
                                        <Typography variant="body2">
                                            No detailed results available. Please check the summary information above.
                                        </Typography>
                                    </Alert>
                                )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowResultsModal(false)} color="primary">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default PodsUploads;